import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, IconName, theme } from '@sg/garnish';

import { usePaymentBreakdown } from '../../../hooks';
import { OrderingLedger } from '../../../machine';
import { CreditPaymentMethod, FlexPaymentMethod, StripePaymentMethod } from './components';

export const BagPaymentMethods = (props: BagPaymentMethodsProps) => {
  const {
    isDisabled = false,
    ledger,
    tip,

    flexPaymentMethod,
    shouldUseFlex,
    amountEligibleForFlex,
    onChangeFlexUsage,

    shouldUseCredit,
    availableCredit,
    appliedCredit,
    onChangeCreditUsage,

    hasNoPaymentMethod,
    stripePaymentMethod,
    stripePaymentMethodIcon,
    stripePaymentMethodImage,
    onPressStripePaymentMethod,
  } = props;

  const paymentBreakdown = usePaymentBreakdown({
    ledger,
    tip,
    availableCredit,
    shouldUseCredit,
    amountEligibleForFlex,
    shouldUseFlex,
  });

  const { formattedTotal } = paymentBreakdown;

  return (
    <View style={styles.container}>
      <View style={styles.title}>
        <BodyText sizeMatch={['14']} bold>
          <FormattedMessage {...messages.title} />
        </BodyText>
      </View>

      <View style={[styles.paymentMethod, styles.borderBottom]}>
        <BodyText sizeMatch={['14']}>
          <FormattedMessage {...messages.total} />
        </BodyText>

        <BodyText sizeMatch={['14']}>{formattedTotal}</BodyText>
      </View>

      {flexPaymentMethod ? (
        <FlexPaymentMethod
          isDisabled={isDisabled}
          flexPaymentMethod={flexPaymentMethod}
          shouldUseFlex={shouldUseFlex}
          amountEligibleForFlex={amountEligibleForFlex}
          onChangeFlexUsage={onChangeFlexUsage}
          paymentBreakdown={paymentBreakdown}
        />
      ) : null}

      {availableCredit > 0 ? (
        <CreditPaymentMethod
          isDisabled={isDisabled}
          shouldUseCredit={shouldUseCredit}
          availableCredit={availableCredit}
          appliedCredit={appliedCredit}
          onChangeCreditUsage={onChangeCreditUsage}
          paymentBreakdown={paymentBreakdown}
        />
      ) : null}

      <StripePaymentMethod
        isDisabled={isDisabled}
        hasNoPaymentMethod={hasNoPaymentMethod}
        stripePaymentMethod={stripePaymentMethod}
        stripePaymentMethodIcon={stripePaymentMethodIcon}
        stripePaymentMethodImage={stripePaymentMethodImage}
        onPressStripePaymentMethod={onPressStripePaymentMethod}
        paymentBreakdown={paymentBreakdown}
      />
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    borderRadius: theme.radius.large,
    borderColor: theme.colors.LINEN,
    borderWidth: 1,
  },
  title: {
    padding: theme.spacing['6'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentMethod: {
    gap: theme.spacing['2'],
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
  },
  borderBottom: {
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.LINEN,
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Payment split',
    description: 'Bag | Payment Methods | Title',
  },
  total: {
    defaultMessage: 'Total',
    description: 'Bag | Payment Methods | Total',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type BagPaymentMethodsProps = {
  isDisabled?: boolean;
  ledger?: OrderingLedger;
  tip: number;

  // HSA/FSA section.

  flexPaymentMethod?: string | null;
  shouldUseFlex: boolean;
  amountEligibleForFlex: number | null;
  onChangeFlexUsage: (shouldUseFlex: boolean) => void;

  // Credit section.

  shouldUseCredit: boolean;
  availableCredit: number;
  appliedCredit: number;
  onChangeCreditUsage: (shouldUseCredit: boolean) => void;

  // Stripe section.

  hasNoPaymentMethod: boolean;
  stripePaymentMethod: string | undefined;
  stripePaymentMethodIcon: IconName | undefined;
  stripePaymentMethodImage: string | undefined;
  onPressStripePaymentMethod?: () => void;
};
